import React, { useState } from 'react';
import { Package, ArrowLeft, Loader2, Search, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePendingDeliveries } from '../../hooks/usePendingDeliveries';
import { useSales } from '../../../../contexts/SalesContext';
import DeliveryStatusModal from '../../../../features/sales/components/DeliveryStatusModal';
import { SuccessScreen } from '../../../../components/feedback/SuccessScreen';
import { SegmentedControl } from '../../../../components/ui/SegmentedControl';

export function PendingDeliveriesScreen() {
  const navigate = useNavigate();
  const { pendingDeliveries, loading, error, setPendingDeliveries } = usePendingDeliveries();
  const { updateSale, getPreparedItems } = useSales();
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');

  const handleDeliveryClick = (sale) => {
    setSelectedSale(sale);
    setShowDeliveryModal(true);
  };

  const handleDeliveryConfirm = async (deliveryInfo) => {
    try {
      setShowDeliveryModal(false);
      setShowSuccess(true);

      const updateData = {
        delivery_status: 'delivered',
        delivered_by: deliveryInfo.staffName,
        delivered_at: new Date().toISOString()
      };

      await updateSale(selectedSale.id, updateData);
      
      // Forzar actualización inmediata filtrando la venta entregada
      setPendingDeliveries(prevDeliveries => 
        prevDeliveries.filter(delivery => delivery.id !== selectedSale.id)
      );
      
      setSelectedSale(null);
    } catch (error) {
      console.error('Error al confirmar la entrega:', error);
      setShowSuccess(false);
    }
  };

  // Filtrar ventas según búsqueda
  const filteredDeliveries = pendingDeliveries.filter(delivery => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      delivery.display_id.toLowerCase().includes(searchLower) ||
      (delivery.client || '').toLowerCase().includes(searchLower) ||
      delivery.sale_items.some(item => 
        (item.lot?.name || '').toLowerCase().includes(searchLower)
      );
    
    const matchesStatus = activeTab === 'pending' 
      ? delivery.delivery_status === 'undelivered'
      : delivery.delivery_status === 'delivered';
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        <p className="mt-2 text-gray-500 dark:text-gray-400">Cargando pedidos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <p className="text-red-500 dark:text-red-400">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-900 dark:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Entregas
        </h1>
      </div>

      {/* Search and Tabs */}
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por número, cliente o producto..."
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>

        {/* Tabs */}
        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'pending', label: 'Pendientes' },
            { value: 'delivered', label: 'Realizadas' }
          ]}
        />
      </div>

      {/* Stats */}
      {activeTab === 'pending' ? (
        // Vista de pedidos pendientes
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Pedidos por preparar */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700">
            <div className="flex flex-col">
              <div className="text-4xl font-bold text-amber-600 dark:text-amber-400">
                {filteredDeliveries.filter(d => {
                  const preparedItems = getPreparedItems(d.id) || new Set();
                  return preparedItems.size < d.sale_items.length;
                }).length}
              </div>
              <div className="text-base text-gray-600 dark:text-gray-400 mt-1">
                pedidos por preparar
              </div>
              <div className="text-sm text-amber-600/80 dark:text-amber-400/80 mt-2">
                {filteredDeliveries
                  .filter(d => {
                    const preparedItems = getPreparedItems(d.id) || new Set();
                    return preparedItems.size < d.sale_items.length;
                  })
                  .reduce((sum, delivery) => 
                    sum + delivery.sale_items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0
                  )} bultos
              </div>
            </div>
          </div>

          {/* Pedidos listos */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700">
            <div className="flex flex-col">
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                {filteredDeliveries.filter(d => {
                  const preparedItems = getPreparedItems(d.id) || new Set();
                  return d.sale_items.length > 0 && preparedItems.size === d.sale_items.length;
                }).length}
              </div>
              <div className="text-base text-gray-600 dark:text-gray-400 mt-1">
                pedidos listos
              </div>
              <div className="text-sm text-blue-600/80 dark:text-blue-400/80 mt-2">
                {filteredDeliveries
                  .filter(d => {
                    const preparedItems = getPreparedItems(d.id) || new Set();
                    return d.sale_items.length > 0 && preparedItems.size === d.sale_items.length;
                  })
                  .reduce((sum, delivery) => 
                    sum + delivery.sale_items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0
                  )} bultos
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Vista de pedidos entregados
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700">
          <div className="flex flex-col">
            <div className="text-4xl font-bold text-green-600 dark:text-green-400">
              {filteredDeliveries.length}
            </div>
            <div className="text-base text-gray-600 dark:text-gray-400 mt-1">
              pedidos entregados
            </div>
            <div className="text-sm text-green-600/80 dark:text-green-400/80 mt-2">
              {filteredDeliveries.reduce((sum, delivery) => 
                sum + delivery.sale_items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0
              )} bultos
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-4">
        {filteredDeliveries.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700 text-center">
            {activeTab === 'pending' && !searchTerm ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 text-green-500 dark:text-green-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.25 4.5a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0V6.31L13.69 12.9a.75.75 0 0 1-1.06 0L9 9.31l-5.03 5.03a.75.75 0 0 1-1.06-1.06l5.5-5.5a.75.75 0 0 1 1.06 0L13 11.31l5.72-5.72h-6.97a.75.75 0 0 1-.75-.75z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  ¡Excelente trabajo!
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Has preparado y entregado todos los pedidos. ¡A descansar! 🍻
                </p>
              </>
            ) : (
              <>
                <Package className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm 
                    ? 'No se encontraron pedidos que coincidan con la búsqueda'
                    : activeTab === 'pending'
                      ? 'No hay pedidos pendientes de entrega'
                      : 'No hay pedidos entregados'}
                </p>
              </>
            )}
          </div>
        ) : (
          filteredDeliveries.map(delivery => {
            const preparedItems = getPreparedItems(delivery.id);
            const totalItems = delivery.sale_items.length;
            const preparedCount = preparedItems.size;
            const preparationProgress = totalItems > 0 ? (preparedCount / totalItems) * 100 : 0;

            return (
              <div 
                key={delivery.id}
                onClick={() => activeTab === 'pending' 
                  ? handleDeliveryClick(delivery)
                  : navigate(`/sales/${delivery.id}`)
                }
                className={`bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors`}
              >
                {/* Header con información principal */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white uppercase">
                      {delivery.client}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="text-lg font-medium text-gray-600 dark:text-gray-300 uppercase">
                        VENTA #{delivery.display_id}
                      </div>
                      <div className="text-lg font-medium text-gray-600 dark:text-gray-300 uppercase">
                        {delivery.sale_items.reduce((sum, item) => sum + item.quantity, 0)} BULTOS
                      </div>
                    </div>
                  </div>
                  <div className={`self-start sm:self-center px-4 py-2 rounded-lg text-lg font-bold uppercase whitespace-nowrap flex items-center gap-2 ${
                    delivery.delivery_status === 'delivered'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : preparedCount === totalItems
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                  }`}>
                    <span>
                      {delivery.delivery_status === 'delivered'
                        ? 'ENTREGADO'
                        : preparedCount === totalItems
                          ? 'LISTO PARA ENTREGAR'
                          : 'PREPARAR'
                      }
                    </span>
                    {(delivery.delivery_status === 'delivered' || preparedCount === totalItems) && (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </div>
                </div>

                {/* Lista de items a preparar */}
                {activeTab === 'pending' && (
                  <div className="mt-4 space-y-2">
                    {delivery.sale_items.map((item, index) => (
                      <div 
                        key={index}
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          preparedItems.has(item.id)
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'bg-gray-50 dark:bg-gray-700/30'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          preparedItems.has(item.id)
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}>
                          •
                        </div>
                        <div className="text-lg uppercase">
                          <span className="font-bold">{item.quantity}x</span>
                          {' '}
                          <span className="text-gray-700 dark:text-gray-300">
                            {item.lot?.name || item.title}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Barra de progreso */}
                {activeTab === 'pending' && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-green-500 dark:bg-green-600 h-2 rounded-full transition-all"
                        style={{ width: `${preparationProgress}%` }}
                      />
                    </div>
                    <div className="mt-2 text-right text-lg font-medium text-gray-600 dark:text-gray-400">
                      {preparedCount}/{totalItems} PREPARADOS
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <DeliveryStatusModal
        isOpen={showDeliveryModal}
        onClose={() => {
          setShowDeliveryModal(false);
          setSelectedSale(null);
        }}
        products={selectedSale?.sale_items || []}
        onDeliveryConfirm={handleDeliveryConfirm}
        sale={selectedSale}
      />

      {/* Success Screen */}
      <SuccessScreen 
        show={showSuccess}
        variant="delivery"
        onClose={() => setShowSuccess(false)}
      />
    </div>
  );
} 